import { useLogEntryDelete } from '@monorepo/logs/src/hooks/useLogEntryDelete';
import { useToast } from '@monorepo/shared/contexts';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { format } from 'date-fns';
import { DateLike, localEquivalentOfUTC } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';

type UseConfirmAndLogEntryDeleteProps = {
  logDate?: DateLike;
  logEntryId: string;
  logId: string;
  onAfterDelete?: () => void;
  organizationId: string;
  projectId: string;
};

export function useConfirmAndLogEntryDelete({
  logDate,
  logEntryId,
  logId,
  onAfterDelete,
  organizationId,
  projectId,
}: UseConfirmAndLogEntryDeleteProps) {
  const formattedLogDate = useMemo(
    () => logDate && format(localEquivalentOfUTC(new Date(logDate)), 'Pp'),
    [logDate],
  );
  const [deleter] = useLogEntryDelete({ config: { throwOnError: true } });
  const handleDeleteConfirm = useCallback(
    async (isConfirmed) => {
      if (isConfirmed) {
        await deleter({
          logEntryId,
          logId,
          organizationId,
          projectId,
        });
      }
    },
    [deleter, logEntryId, logId, organizationId, projectId],
  );
  const { confirm } = useModal();
  const { success, showUserFriendlyErrorToast } = useToast();
  const handleDelete = useCallback(async () => {
    try {
      const isConfirmed = await confirm({
        title: 'Delete Log entry',
        description: (
          <>
            Are you sure you want to delete the entry from{' '}
            <b>{formattedLogDate}</b>?
          </>
        ),
        cancelButtonText: 'Keep Log entry',
        confirmButtonText: 'Delete Log entry',
        afterConfirmButtonText: 'Deleting...',
        danger: true,
        onConfirmAsync: handleDeleteConfirm,
      });
      if (isConfirmed) {
        success(`Entry from ${formattedLogDate} has been deleted.`);
        onAfterDelete && onAfterDelete();
      }
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to delete entry from ${formattedLogDate}.`,
        { dontAutoHide: true },
      );
    }
  }, [
    confirm,
    formattedLogDate,
    handleDeleteConfirm,
    onAfterDelete,
    success,
    showUserFriendlyErrorToast,
  ]);

  return { handleDelete };
}
